import { ConfirmDialog } from '@clickguard/design-system'
import { useContext, useEffect, useState } from 'react'
import { UNSAFE_NavigationContext } from 'react-router-dom'

/**
 * Warns before unsaved settings are thrown away (DEV-2422 §1.4 — "Leaving with
 * unsaved changes must warn the user, do not discard silently").
 *
 * ONE mechanism for all three exits: `history.block()`.
 *
 *   - In-app navigation (a sidebar row, a `<Link>`, any `navigate()`) — the router
 *     calls `navigator.push` / `.replace`, and a registered blocker turns that into
 *     a callback with a `retry` instead of a navigation.
 *   - Browser Back / Forward — history reverts the POP itself and hands the same
 *     `retry` shape back, so both look identical to us.
 *   - Tab close / reload / a typed URL — `block()` installs its own `beforeunload`
 *     listener (`promptBeforeUnload`), which is the browser's dialog and cannot be
 *     styled or relabelled.
 *
 * `history.block` and not `useBlocker`: this repo is on react-router 6.1.1, which
 * predates it (unstable in 6.7, stable in 6.19). But `BrowserRouter` hands the raw
 * `history@5` object to `UNSAFE_NavigationContext` as its `navigator`, and that
 * object still carries `block()` — which is what `useBlocker` was built on. Drop
 * this file's context read for the real hook whenever react-router is upgraded.
 *
 * This replaced a hand-rolled history SENTINEL that only caught Back and reload.
 * In-app navigation never fires `popstate`, so clicking any sidebar row discarded
 * the draft silently — which is the one exit a user actually takes. The sentinel
 * also called `history.back()` from its own cleanup, which React 18 StrictMode's
 * double-invoked effects fire spuriously in development.
 */

/** The `retry` half of history's transaction; the rest is not needed here. */
type BlockedTx = { retry: () => void }

/**
 * React Router types `navigator` as its own narrow `Navigator`, which stops at
 * push/replace/go. The object underneath is the full history, so this names the one
 * method we reach past that type for.
 */
type BlockingNavigator = {
	block: (blocker: (tx: BlockedTx) => void) => () => void
}

export const UnsavedChangesGuard = ({ when }: { when: boolean }) => {
	const { navigator } = useContext(UNSAFE_NavigationContext)
	// The blocked navigation, held while the dialog is up. Storing the transaction
	// rather than a boolean is what lets "Discard" complete the exact navigation the
	// user asked for — including a Back press, whose retry is a `go(-1)` we could not
	// reconstruct from here.
	const [blocked, setBlocked] = useState<BlockedTx | null>(null)

	useEffect(() => {
		if (!when) return

		const unblock = (navigator as unknown as BlockingNavigator).block((tx) => {
			setBlocked({
				// Unblock BEFORE retrying, or the retry trips this same blocker and the
				// dialog reopens forever. `unblock` is idempotent, and the effect cleanup
				// below calls it again on the unmount this navigation causes.
				retry: () => {
					unblock()
					tx.retry()
				}
			})
		})

		return unblock
	}, [when, navigator])

	return (
		<ConfirmDialog
			open={!!blocked}
			// Keep editing. The blocker stays registered, so the next attempt is caught
			// too — nothing to re-arm.
			onClose={() => setBlocked(null)}
			onConfirm={() => {
				blocked?.retry()
				setBlocked(null)
			}}
			tone="danger"
			title="Discard unsaved changes?"
			description="Your protection settings have changed since the last save. Leaving this page will lose them."
			confirmLabel="Discard changes"
			cancelLabel="Keep editing"
		/>
	)
}
